import { CurrentStepEnum } from "@boundless/bl-api-client/customer/models/CurrentStepEnum"
import { OnboardingDebtScheduleStateEnum } from "@boundless/bl-api-client/customer/models/OnboardingDebtScheduleStateEnum"
import { Mutation, Query } from "@tanstack/query-core"
import { type ClassValue, clsx } from "clsx"
import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import { twMerge } from "tailwind-merge"

import { StepPaths } from "@/lib/constants"
import type { BoundlessRouter } from "@/main"

import type { Company } from "@boundless/bl-api-client/customer/models/Company"
import type { NavigateOptions } from "@tanstack/react-router"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

dayjs.extend(advancedFormat)

/**
 * @summary Imperative redirect to a URL or Path using `window`.
 *
 * @desc This should be only used when outside of RouteProvider, otherwise
 * `Route.navigate` should be favored. This function makes it easier to test.
 * */
export function redirectTo(href: string) {
  window.location.href = href
}

export function formatDate(dateString: string | undefined | null): string {
  if (!dateString) {
    return ""
  }
  return dayjs(dateString).format("MMM Do, YYYY")
}

/** Date and time, for records where the order within a day matters. */
export function formatDateTime(dateString: string | undefined | null): string {
  if (!dateString) {
    return ""
  }
  return dayjs(dateString).format("MMM Do, YYYY h:mm A")
}

/**
 * @summary Retry function factory for use with TanStack Query.
 *
 * @desc This factory makes it easier to define retry functions for TanStack
 * Query, by standardizing some common checks.
 *
 * @example
 * // Basic usage: no retry for error 404, otherwise retry only once
 * retry: shouldRetryFactory({ skippedStatuses: [404], maxRetries: 1 })
 *
 * @example
 * // Basic usage: retry at least once, regardless of the status (even on
 * server errors)
 * retry: shouldRetryFactory({ skipServerError: false, maxRetries: 1 })
 *
 * @example
 * // Default behavior: no retry for server errors, otherwise retry up to 2 times
 * retry: shouldRetryFactory()
 * */
export function shouldRetryFactory({
  maxRetries = 3,
  skipServerError = true,
  skippedStatuses = [],
}: {
  maxRetries?: number
  skipServerError?: boolean
  skippedStatuses?: number[]
} = {}): (count: number, error: Error) => boolean {
  return (count: number, error: Error) => {
    if ("status" in error && typeof error.status === "number") {
      // During development, always retry on 502 errors, since they're common
      // while reloading the backend
      if (import.meta.env.DEV && error.status === 502) {
        return true
      }

      if (skippedStatuses.includes(error.status)) {
        return false
      }

      if (skipServerError && error.status >= 500) {
        return false
      }
    }

    return count < maxRetries
  }
}

export type QueryOrMutation =
  | Query<unknown, Error, unknown>
  | Mutation<unknown, Error, unknown, unknown>

export type BoundlessQueryMeta = {
  shouldDisplayErrorToast?:
    | boolean
    | ((error: Error, source: QueryOrMutation) => boolean)
}

function getQueryMeta(source: QueryOrMutation): BoundlessQueryMeta | undefined {
  if ("meta" in source && source.meta) {
    return source.meta as BoundlessQueryMeta
  }

  if ("options" in source && source.options?.meta) {
    return source.options.meta as BoundlessQueryMeta
  }

  return undefined
}

/**
 * @summary Decide whether the global "Something went wrong" toast should fire.
 *
 * @desc The handler first checks the query or mutation's `shouldDisplayErrorToast`
 * meta override. Accepts a boolean (force show/hide) or a predicate for custom
 * filtering. When no override is provided, the default behaviour only emits for
 * server errors (status ≥ 500), Forbidden errors (status 403) and rate-limited
 * requests (status 429).
 *
 * @example
 * useCustomerPlaidLinkRetrieve(companyUuid, {
 *   query: { meta: { shouldDisplayErrorToast: false } },
 * })
 *
 * @example
 * useCustomerPlaidExchangeCreate({
 *   mutation: {
 *     meta: {
 *       shouldDisplayErrorToast: (error) => !isExpectedPlaidError(error),
 *     },
 *   },
 * })
 */
export function shouldDisplayToast(
  error: Error,
  source: QueryOrMutation,
): boolean {
  const meta = getQueryMeta(source)
  const shouldDisplayErrorToast = meta?.shouldDisplayErrorToast

  if (shouldDisplayErrorToast !== undefined) {
    switch (typeof shouldDisplayErrorToast) {
      case "boolean":
        return shouldDisplayErrorToast
      case "function":
        return shouldDisplayErrorToast(error, source)
      default:
        // Log warning and fall through to default behavior
        console.warn(
          "Invalid shouldDisplayErrorToast type",
          shouldDisplayErrorToast,
        )
    }
  }

  return isToastableError(error)
}

/**
 * @summary The default toast rule, for `shouldDisplayErrorToast` predicates
 * that only need to exclude some errors, since a predicate replaces it.
 */
export function isToastableError(error: Error): boolean {
  if (!("status" in error)) {
    return false
  }
  const status = error.status as number
  return status >= 500 || status === 403 || status === 429
}

export function isRateLimitedError(error: Error): boolean {
  return "status" in error && error.status === 429
}

export function getCurrentStepPath(
  currentStep: CurrentStepEnum,
  applicationUuid: string | null,
  fundingNeedsUuid: string | null,
): NavigateOptions<BoundlessRouter> {
  return (
    StepPaths[currentStep]?.({
      applicationUuid: applicationUuid ?? "",
      fundingNeedsUuid: fundingNeedsUuid ?? "",
    }) ?? "/company"
  )
}

export function sortByLabel<T extends { label: string }>(
  items: T[],
  options: { sortLast?: Array<string> } = {
    sortLast: ["Other"],
  },
): T[] {
  const { sortLast = ["Other"] } = options

  return items.sort((a, b) => {
    const labelA = a.label
    const labelB = b.label
    if (sortLast?.includes(labelA) && sortLast?.includes(labelB)) {
      return 0
    }
    if (sortLast?.includes(labelA)) return 1
    if (sortLast?.includes(labelB)) return -1
    return labelA.localeCompare(labelB)
  })
}

export function isEmbed(): boolean {
  return document.querySelector("boundless-embed") !== null
}

export function getPublicAssetUrl(path: string): string {
  const baseUrl = import.meta.env.VITE_PUBLIC_ASSET_BASE_URL
  return baseUrl ? `${baseUrl}${path}` : path
}

/**
 * Returns the correct DOM element to attach theme CSS variables to.
 *
 * In the regular app, theme variables are written to <html>.
 * In the embedded app, the UI lives inside a ShadowRoot, so variables
 * must be written to the ShadowRoot host element instead.
 *
 * CSS variables do not cross Shadow DOM boundaries.
 */
export function getThemeTarget(
  portalContainer: HTMLElement | null,
): HTMLElement {
  if (portalContainer && portalContainer.getRootNode() instanceof ShadowRoot) {
    return (portalContainer.getRootNode() as ShadowRoot).host as HTMLElement
  }

  return document.documentElement
}

/**
 * Comparator for sorting banking statement documents
 * with `referenceAccount` as a tiebreaker for statements of the same date.
 *
 * Sort order:
 * 1. referenceDate descending
 * 2. referenceAccount ascending
 *
 * Entries with a null referenceDate or referenceAccount are sorted to the end.
 *
 * @example
 * documents.sort(compareStatements)
 */
export function compareStatements(
  a: { referenceDate: string | null; referenceAccount: number | null },
  b: { referenceDate: string | null; referenceAccount: number | null },
): number {
  if (!a.referenceDate && !b.referenceDate) return 0
  if (!a.referenceDate) return 1
  if (!b.referenceDate) return -1

  const dateCompare = b.referenceDate.localeCompare(a.referenceDate)
  if (dateCompare !== 0) return dateCompare

  if (!a.referenceAccount && !b.referenceAccount) return 0
  if (!a.referenceAccount) return 1
  if (!b.referenceAccount) return -1

  return a.referenceAccount - b.referenceAccount
}

const DEBT_CONFIRMATION_MAX_AGE_DAYS = 30

/** Whether the borrower should reconfirm their Debt Answer. */
export function needsDebtConfirmation(company: Company): boolean {
  const confirmable =
    company.onboardingDebtScheduleState ===
      OnboardingDebtScheduleStateEnum.submitted ||
    company.onboardingDebtScheduleState ===
      OnboardingDebtScheduleStateEnum["no-debt"] ||
    company.onboardingDebtScheduleState ===
      OnboardingDebtScheduleStateEnum.skipped
  if (!confirmable) {
    return false
  }
  if (!company.debtScheduleConfirmedAt) {
    return true
  }
  return (
    dayjs().diff(company.debtScheduleConfirmedAt, "day") >=
    DEBT_CONFIRMATION_MAX_AGE_DAYS
  )
}
